import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import SquishyButton from "../components/SquishyButton";
import { speak } from "../lib/speech";
import { sound } from "../lib/sounds";
import { Eraser, Trash2 } from "lucide-react";

const PALETTE = ["#111827", "#EF4444", "#FB923C", "#FDE047", "#4ADE80", "#38BDF8", "#A78BFA", "#F472B6", "#FFFFFF"];
const SIZES = [6, 14, 26];

// A real free-drawing canvas: pick colours + brush size and draw.
export default function DrawCanvas({ onFinish }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [color, setColor] = useState("#EF4444");
  const [size, setSize] = useState(14);
  const [erase, setErase] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const data = canvas.toDataURL();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      if (data && data.length > 100) img.src = data;
    };
    resize();
    speak("Teken je eigen kunstwerk! Kies een kleur en begin.");
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => { drawing.current = true; last.current = pos(e); draw(e); };
  const end = () => { drawing.current = false; last.current = null; };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = erase ? "#FFFFFF" : color;
    ctx.lineWidth = erase ? size * 2.2 : size;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const clear = () => {
    sound.pop();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {/* colour palette */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {PALETTE.map((c) => (
          <motion.button
            key={c} data-testid={`draw-color-${c}`} whileTap={{ scale: 0.85 }}
            onClick={() => { setColor(c); setErase(false); sound.tap(); }}
            className={`h-9 w-9 rounded-full border-4 ${color === c && !erase ? "border-slate-700" : "border-white"} shadow`}
            style={{ backgroundColor: c }}
          />
        ))}
        <motion.button data-testid="draw-eraser" whileTap={{ scale: 0.85 }}
          onClick={() => { setErase(true); sound.tap(); }}
          className={`flex h-9 w-9 items-center justify-center rounded-full border-4 ${erase ? "border-slate-700" : "border-white"} bg-slate-100 shadow`}>
          <Eraser className="h-4 w-4 text-slate-500" />
        </motion.button>
      </div>

      {/* brush sizes + clear */}
      <div className="flex items-center gap-3">
        {SIZES.map((s) => (
          <motion.button key={s} data-testid={`draw-size-${s}`} whileTap={{ scale: 0.85 }}
            onClick={() => { setSize(s); setErase(false); sound.tap(); }}
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-white shadow ${size === s ? "ring-2 ring-sky-400" : ""}`}>
            <span className="rounded-full bg-slate-700" style={{ width: s, height: s }} />
          </motion.button>
        ))}
        <button data-testid="draw-clear" onClick={clear}
          className="flex items-center gap-1 rounded-full bg-white px-3 py-2 font-bold text-slate-500 shadow">
          <Trash2 className="h-4 w-4" /> Wissen
        </button>
      </div>

      <canvas
        ref={canvasRef}
        data-testid="draw-canvas"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        className="h-[46vh] w-full max-w-lg touch-none rounded-3xl bg-white shadow-lg"
      />

      <SquishyButton data-testid="draw-done-btn" onClick={() => { sound.reward(); onFinish(1, 1); }}
        color="#4ADE80" edge="#16A34A" className="text-xl px-10">
        Klaar! 🎨
      </SquishyButton>
    </div>
  );
}
